// frontend/src/main.jsx
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ClerkProvider, ClerkLoaded, ClerkLoading } from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";

import App from "./App.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import { Notifier } from "./components/Notify.jsx";
import { initTenantSlug } from "./lib/tenant.js";
import { ThemePreferenceProvider } from "./theme/ThemePreference.jsx";

import "/src/index.css";
import { initSentry } from "./sentry.js";

// -----------------------------------------------------------------------------
// Environment validation (Fail Fast — Google/AWS style)
// -----------------------------------------------------------------------------

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!clerkPublishableKey) {
  throw new Error("[Startup] Missing VITE_CLERK_PUBLISHABLE_KEY. Check your environment variables.");
}

// -----------------------------------------------------------------------------
// Bootstrap
// -----------------------------------------------------------------------------

// Init monitoring first (before React)
initSentry();

// Init tenant context
initTenantSlug();

// Find root safely
const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("[Startup] Root element (#root) not found in index.html");
}

// -----------------------------------------------------------------------------
// App Mount
// -----------------------------------------------------------------------------

const isTenantSatellite = window.location.hostname.endsWith(".localhost") && window.location.hostname !== "localhost";

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ClerkProvider
      publishableKey={clerkPublishableKey}
      isSatellite={isTenantSatellite}
      domain={isTenantSatellite ? window.location.host : undefined}
      signInUrl={isTenantSatellite ? "http://localhost:5173/portal/login" : undefined}
      signUpUrl={isTenantSatellite ? "http://localhost:5173/portal/login" : undefined}
      allowedRedirectOrigins={isTenantSatellite ? ["http://cbcfx.localhost:5173"] : undefined}>
      <ClerkLoading>
        <div>Loading authentication...</div>
      </ClerkLoading>

      <ClerkLoaded>
        <ThemePreferenceProvider>
          <Sentry.ErrorBoundary
            fallback={
              <div className="portal-error">
                <h2>Something went wrong</h2>
                <p>Please refresh the page. If the issue persists, contact support.</p>
              </div>
            }>
            <BrowserRouter>
              <Notifier>
                <App />
              </Notifier>
            </BrowserRouter>
          </Sentry.ErrorBoundary>
        </ThemePreferenceProvider>
      </ClerkLoaded>
    </ClerkProvider>
  </React.StrictMode>,
);
